// infrastructure/api/authorizationApi.js
import { auth } from 'boot/firebase';
import { serverTimestamp } from 'firebase/firestore';
import AuthorizationsDB from 'src/fb/authorizations-db';
import GenericDB from 'src/fb/generic-db';

import { AUTHORIZATION_INTENT } from '../../application/authorizationChange';
import { toAuthorization, toDocument } from '../mappers/authorizationMapper';

/**
 * Persistence for authorizations.
 *
 * `authorizations` is a flat root collection scoped by the document's `client` and `clinic` fields.
 * Those keys are bare rather than `Id`-suffixed because the rules helpers read those exact names.
 *
 * A SEPARATE COLLECTION FROM `clientAuthorizations`, WHICH THIS NEVER WRITES TO. That legacy
 * collection serves the SCHEDULING axis, and `client.hasAuthorizations` is recomputed from it on
 * every client save with the appointment picker branching on the flag — false offers every treatment
 * service, true only authorized ones. So a write into there would REMOVE bookable services from
 * clinics that gain nothing from this bet.
 *
 * EVERY READ MUST FILTER `isDeleted == false`. Removal is a soft delete and the tombstone is not
 * inert: read as live it clears a hold that should have stood.
 *
 * THE READ IS EQUALITY-ONLY and needs no composite index — merged single-field indexes serve it, and
 * the window, service and place of service are compared in memory. The first range filter anyone
 * adds needs a real composite index, shipped before the code.
 */

/**
 * An id for a document not yet written — a letter is staged, edited and possibly removed before the
 * client dialog saves, and can be entered for a client that does not exist yet. Firestore's own
 * generator rather than a UUID, so these read as native ids everywhere they surface.
 *
 * @returns {string}
 */
export function newAuthorizationId() {
  return new AuthorizationsDB().newId();
} // newAuthorizationId()

/**
 * A limit's LOCAL identity. Its own function rather than an alias of the one above, though today
 * they do the same thing: a limit id must never be resolved as a document id, and keeping the
 * generators apart means nothing can come to rely on their being interchangeable.
 *
 * @returns {string}
 */
export function newLimitId() {
  return new AuthorizationsDB().newId();
} // newLimitId()

/**
 * Loads a client's live authorizations.
 *
 * @param {{ clientId: string, clinicId: string }} scope
 * @returns {Promise<import('../../domain/model/Authorization.types').Authorization[]>}
 */
export async function loadClientAuthorizations({ clientId, clinicId }) {
  const documents = await new AuthorizationsDB().readAll([
    ['clinic', '==', clinicId],
    ['client', '==', clientId],
    ['isDeleted', '==', false],
  ]);

  return documents.map(toAuthorization);
} // loadClientAuthorizations()

/**
 * The platform's soft-delete trio, written explicitly rather than read off GenericDB's
 * `updateTimestamp` / `updateBy`, which every later write overwrites. The names match exactly
 * because a retention sweep is cross-collection and would skip a tombstone called anything else.
 *
 * `deleteTimestamp` is a Firestore `Timestamp`, NOT a calendar string — the letter's window is
 * calendar dates and must never be instants, whereas a deletion is a moment.
 *
 * `auth` ITSELF IS OPTIONAL-CHAINED, not just `currentUser`: it is a mutable export, and switching
 * region tears the Firebase app down, so it is null for a window. This stops a TypeError taking the
 * whole transaction down; it does not rescue the write, since `isTombstonedByCaller` requires
 * `deletedBy` to be the caller and a null is refused. The failure is a permission error the caller
 * can retry, rather than a crash mid-transaction.
 */
const tombstone = () => ({
  isDeleted: true,
  deleteTimestamp: serverTimestamp(),
  deletedBy: auth?.currentUser?.uid ?? null,
});

/**
 * Applies a set of staged changes in a single transaction.
 *
 * `executeTransaction` carries the offline path and the IndexedDB mirror that a hand-rolled
 * `writeBatch` here would lose. It must be called on a plain GenericDB, not a subclass.
 *
 * AN UPDATE REWRITES THE WHOLE DOCUMENT, because an edit may touch any part of the letter. The cost
 * is LAST-WRITE-WINS: two billers editing one authorization in different sessions do not conflict,
 * the later save simply stands. That is the same trade `authorizationChange` records for the verb
 * itself, and it expires on the same day.
 *
 * @param {import('../../application/authorizationChange').AuthorizationChange[]} changes
 * @returns {Promise<unknown>}
 */
export function applyChanges(changes) {
  if (changes.length === 0) {
    return Promise.resolve();
  }

  const authorizationsDb = new AuthorizationsDB();

  return new GenericDB().executeTransaction(async (transaction) => {
    // No reads, so Firestore's reads-before-writes ordering rule is not a concern.
    await Promise.all(changes.map(({ intent, authorization }) => {
      switch (intent) {
        case AUTHORIZATION_INTENT.CREATE:
          // The staged id becomes the document id, so an authorization keeps one identity from the
          // moment the biller adds it through to whatever a claim later resolves against it.
          return transaction.create(authorizationsDb, {
            data: toDocument(authorization),
            id: authorization.authorizationId,
          });
        case AUTHORIZATION_INTENT.UPDATE:
          return transaction.update(authorizationsDb, {
            data: { ...toDocument(authorization), id: authorization.authorizationId },
          });
        case AUTHORIZATION_INTENT.DELETE:
          return transaction.update(authorizationsDb, {
            data: { id: authorization.authorizationId, ...tombstone() },
          });
        default:
          // A change with no recognized intent is a programming error, and swallowing it is how a
          // save reports success having written nothing. Fail the transaction instead.
          throw new TypeError(`applyChanges: unknown authorization intent "${intent}"`);
      }
    }));
  });
} // applyChanges()
